/**
 * Two-step sign-in setup (approved mockup v10, Bill 2026-10-27). Shown full-page to anyone who
 * signs in with a password and hasn't turned it on (DocVault won't open until they do), and
 * inside My profile to move it to a new phone. Cognito keeps the authenticator; DocVault records
 * it once Cognito confirms (POST /me/mfa/confirm).
 */
import { useEffect, useState } from "react";
import qrcode from "qrcode-generator";
import { api } from "../lib/api";
import { useAuth } from "./AuthProvider";
import { LOCAL_AUTH, cognitoMessage, finishTotpSetup, startTotpSetup } from "./cognito";
import { LoginArt } from "./LoginArt";

const group = (s: string) => s.replace(/(.{4})/g, "$1 ").trim();

export function MfaSetupPanel({ email, onDone, onCancel }: { email: string; onDone: () => void; onCancel?: () => void }) {
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (LOCAL_AUTH) { setSecret("JBSWY3DPEHPK3PXP6K4L"); return; }
    startTotpSetup().then(setSecret, (e) => setErr(cognitoMessage(e)));
  }, []);
  const qr = (() => {
    if (!secret) return "";
    const q = qrcode(0, "M");
    q.addData(`otpauth://totp/DocVault:${encodeURIComponent(email)}?secret=${secret}&issuer=DocVault`);
    q.make();
    return q.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  })();
  const turnOn = async () => {
    const c = code.replace(/\s+/g, "");
    if (!/^\d{6}$/.test(c)) return setErr("Enter the 6-digit code your app shows.");
    setErr(""); setBusy(true);
    try {
      if (!LOCAL_AUTH) await finishTotpSetup(c);
      await api.mfaConfirm();
      onDone();
    } catch (e) { setErr((e as { code?: string }).code ? cognitoMessage(e) : (e as Error).message); }
    finally { setBusy(false); }
  };
  const copy = () => { if (secret) void navigator.clipboard?.writeText(secret).then(() => setCopied(true), () => setCopied(false)); };
  return (
    <div className="mfa-setup">
      <div><b>1. Get an authenticator app</b><div className="help">Microsoft Authenticator, Google Authenticator, 1Password or any app that shows 6-digit codes.</div></div>
      <div><b>2. Scan this code with the app</b>
        <div className="mfa-qr-row">
          {secret ? <div className="mfa-qr" aria-label="QR code for your authenticator app" dangerouslySetInnerHTML={{ __html: qr }} /> : <div className="mfa-qr mfa-qr-wait">{err ? "" : "Preparing…"}</div>}
          <div className="help" style={{ maxWidth: 230 }}>Can't scan? Choose "Enter a setup key" in the app and type:
            <div className="mono mfa-key">{secret ? group(secret) : "…"}</div>
            <button className="btn btn-ghost btn-sm" type="button" onClick={copy} disabled={!secret}>{copied ? "Copied" : "Copy key"}</button></div>
        </div>
      </div>
      <label><b>3. Enter the 6-digit code it shows</b>
        <input className="inp mono mfa-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000 000" inputMode="numeric" autoComplete="one-time-code" maxLength={7}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void turnOn(); } }} /></label>
      <div className="err" role="alert">{err}</div>
      <div className="row-btns" style={{ margin: 0 }}>
        <button className="btn btn-accent" type="button" disabled={busy || !secret} onClick={() => void turnOn()}>{busy ? "Checking…" : "Turn on and continue"}</button>
        {onCancel && <button className="btn btn-ghost" type="button" onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

/** Full page, in the sign-in layout, for the first sign-in after two-step became required. */
export function MfaSetupPage() {
  const { me, refresh, signOut } = useAuth();
  return (
    <div className="login">
      <LoginArt />
      <div className="form-side">
        <form onSubmit={(e) => e.preventDefault()}>
          <h1>Set up two-step sign-in</h1>
          <p className="help" style={{ marginTop: -4 }}>DocVault asks for a code from your phone each time you sign in with a password. This takes about a minute, once.</p>
          <MfaSetupPanel email={me?.user.email ?? ""} onDone={() => void refresh()} />
          <button type="button" className="linkbtn" onClick={signOut}>Sign out</button>
          <p className="access">Signing in with Microsoft instead? You won't see this: your company's Microsoft settings handle the second step.</p>
        </form>
      </div>
    </div>
  );
}
